import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';
import { MessageService } from 'src/app/message/services/message.service';
import { fadeIn, fadeOut } from 'src/app/shared/animations';
import {
    Accessibility,
    ContainerCreateRequest,
    ContainerCreateResponse,
    ContainerType,
    Visibility,
} from 'src/app/shared/models/container.types';
import {
    WizardSlide,
    WizardSlideContentElement,
    WIZARD_COLLABORATION_SLIDE_ID,
    WIZARD_FORM_SLIDE_ID,
} from '../../models/wizard-slide';
import { ContainerWizardSlideCreationService } from '../../services/container-wizard-slide-creation.service';
import { WizardFormData as WizardFormData } from 'src/app/shared/components/wizard-form/wizard-form.data';
import { CarouselComponentHandler } from 'src/app/shared/components/carousel/carousel.component';
import { ContainerService } from 'src/app/shared/services/container/container.service';
import { getContainerRouteSegment } from 'src/app/shared/models/container-navigation';
import { AuthenticationService } from 'src/app/authentication/services/authentication.service';

@Component({
    selector: 'app-wizard-slide',
    templateUrl: './wizard-slide.component.html',
    styleUrls: ['./wizard-slide.component.scss'],
    providers: [ContainerWizardSlideCreationService],
    animations: [fadeIn, fadeOut],
    standalone: false,
})
export class WizardSlideComponent implements CarouselComponentHandler, OnInit {
    @Input() wizardList: Array<WizardSlide>;
    @Input() activeWizardSlide: number = 1;
    @Input() type: ContainerType;

    @Output()
    activeWizardPosition: EventEmitter<number> = new EventEmitter<number>();
    @Output()
    addProject: EventEmitter<string> = new EventEmitter<string>();

    hasSubmitFailed = new BehaviorSubject<boolean>(false);
    createDto: ContainerCreateRequest = new ContainerCreateRequest();

    entityTranslationKey: string;
    selectedVisibility: Visibility = Visibility.PUBLIC;
    selectedAccessibility: Accessibility = Accessibility.CLOSED;
    availableVisibilityOptions: Visibility[] = [];
    availableAccessibilityOptions: Accessibility[] = [
        Accessibility.CLOSED,
        Accessibility.OPEN,
    ];

    constructor(
        private containerService: ContainerService,
        private messageService: MessageService,
        private router: Router,
        private translate: TranslateService,
        private containerWizardService: ContainerWizardSlideCreationService,
        private authenticationService: AuthenticationService
    ) {}

    ngOnInit(): void {
        this.createDto.containerType = this.type;
        this.entityTranslationKey =
            ContainerType.IMAGE_DB === this.type
                ? 'imgDb.wizard'
                : 'project.components';
        this.initializeCollaborationOptions();
    }

    onSubmit(finalData: WizardFormData): void {
        const defaultTrainingName = this.translate.instant(
            'splitScreen.defaultTrainingName'
        );

        this.createDto.name = finalData.name;
        this.createDto.description = finalData.description;
        this.createDto.defaultTrainingName = defaultTrainingName;
        this.createDto.groupId = finalData.groupId;
        this.createDto.collaborationOptionsDto = {
            visibility: this.selectedVisibility,
            accessibility: this.selectedAccessibility,
        };

        this.containerService.create(this.createDto).subscribe({
            next: (data: ContainerCreateResponse) => {
                if (data !== undefined) {
                    this.confirmCreatedProject(data.name);
                    this.redirectToContainer(this.type, data.id);
                }
            },
            error: (err) => {
                console.error(err);
                this.hasSubmitFailed.next(true);
                throw err;
            },
        });
    }

    private redirectToContainer(containerType: ContainerType, id: string) {
        this.router.navigate([
            getContainerRouteSegment(containerType),
            containerType,
            id,
        ]);
    }

    private confirmCreatedProject(projectName: string) {
        const translateKey =
            ContainerType.IMAGE_DB === this.type
                ? 'imgDb.events.createSuccess'
                : 'project.components.projectEvents.createSuccess';
        this.messageService.displaySuccessMessage(
            this.translate.instant(translateKey, { name: projectName })
        );

        this.addProject.emit('closeModal');
    }

    chooseOption(elem: WizardSlideContentElement) {
        if (elem.redirectSlideId === 0) {
            return;
        }
        if (elem.redirectSlideId === WIZARD_COLLABORATION_SLIDE_ID) {
            this.createDto.parameter = <string>elem.cardValue;
        }

        this.changeWizard(elem.redirectSlideId);
        this.activeWizardPosition.emit(elem.redirectSlideId);
    }

    changeWizard(id: number): void {
        this.activeWizardSlide = id;

        this.wizardList = this.containerWizardService.changeSlideData(
            this.createDto
        );
    }

    onCollaborationContinue() {
        this.createDto.collaborationOptionsDto = {
            visibility: this.selectedVisibility,
            accessibility: this.selectedAccessibility,
        };
        this.changeWizard(WIZARD_FORM_SLIDE_ID);
        this.activeWizardPosition.emit(WIZARD_FORM_SLIDE_ID);
    }

    onVisibilityChange(option: Visibility) {
        if (this.selectedVisibility !== option) {
            this.selectedVisibility = option;
            this.createDto.collaborationOptionsDto.visibility = option;
        }
    }

    onAccessibilityChange(option: Accessibility) {
        if (this.selectedAccessibility !== option) {
            this.selectedAccessibility = option;
            this.createDto.collaborationOptionsDto.accessibility = option;
        }
    }

    visibilityKey(option: Visibility): string {
        return Visibility[option];
    }

    accessibilityKey(option: Accessibility): string {
        return Accessibility[option];
    }

    onGoBack() {
        this.changeWizard(--this.activeWizardSlide);
    }

    getSelectionHandler(): CarouselComponentHandler {
        return this;
    }

    isActive(elem: WizardSlideContentElement): boolean {
        if (!elem) {
            return false;
        }
        if (elem.redirectSlideId === WIZARD_COLLABORATION_SLIDE_ID) {
            return elem.cardValue === this.createDto.parameter;
        }
        return false;
    }

    private initializeCollaborationOptions() {
        const hasOrganization =
            !!this.authenticationService.currentUserValue?.activeOrganization;
        this.availableVisibilityOptions = hasOrganization
            ? [Visibility.PRIVATE, Visibility.GROUP, Visibility.ORG]
            : [Visibility.PRIVATE, Visibility.PUBLIC];

        this.selectedVisibility =
            this.createDto?.collaborationOptionsDto?.visibility ??
            Visibility.PRIVATE;
        this.selectedAccessibility =
            this.createDto?.collaborationOptionsDto?.accessibility ??
            Accessibility.CLOSED;
        this.createDto.collaborationOptionsDto = {
            visibility: this.selectedVisibility,
            accessibility: this.selectedAccessibility,
        };
    }
}
